import React, { useRef, useState, useCallback } from "react";
import { Drawer, Button, message } from "antd";
import { useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import VDPSelector from "../vdpSelector";
import { CREATE_OTTOVIN_LEAD_LINKS } from "../../../../graphql/mutation";
import { resolveLeadOpportunityId } from "../../../../helpers/opportunityHelper";

/**
 * OttoVin panel for the Activity Center's Otto Links menu. Same vehicle
 * search/selection as the VDP panel (shared VDPSelector), but "Add Links"
 * mints OttoVin links attributed to the current lead and the sending
 * salesperson — already shortened to EngagedAI short URLs server-side — and
 * inserts them into the composer.
 */
const OttoVinPanel = ({
  open,
  onClose,
  text,
  setmessageText,
  handleInsertLink,
  selectedLead,
  selectedOppTypeId,
}) => {
  const selectorRef = useRef();
  // The pinned opportunity (if any) from the message composer; wins over the
  // opp-type tab. The backend resolves the lead-opportunity from whichever we send.
  const { messageOpportunity } = useSelector(state => state.lead);

  // The lead's vehicle of interest, from the most recent opportunity that has
  // one (preferring the primary, then the current VOI). Seeds the selector so
  // the VOI shows first with similar inventory beneath it.
  const vehicleOfInterest = React.useMemo(() => {
    const opportunities = selectedLead?.leadOpportunities || [];
    const all = opportunities.flatMap(opp => opp?.vehiclesOfInterest || []);
    if (all.length === 0) return null;
    return (
      all.find(voi => voi?.isPrimary) ||
      all.find(voi => voi?.isCurrent) ||
      all.find(voi => voi?.model || voi?.make) ||
      all[0]
    );
  }, [selectedLead]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createOttoVinLeadLinks] = useMutation(CREATE_OTTOVIN_LEAD_LINKS);

  const onSelectedVehiclesChange = useCallback(vehicles => {
    setSelectedVehicles(vehicles);
  }, []);

  // Resolver handed to VDPSelector: selected vehicle ids -> { VIN: shortUrl }
  // map (or null on failure; nothing is inserted then). VDPSelector composes
  // each link into a "Year Make Model Stock# link" line from this map.
  const resolveLinks = useCallback(
    async vehicleIds => {
      if (!selectedLead?.id) {
        message.error("Select a lead before adding OttoVin links.");
        return null;
      }
      // Pinned opportunity wins; otherwise send the root opp-type id and let the
      // backend resolve the lead-opportunity (util.resolve_lead_opportunity_id) —
      // mirrors the message-send flow (BottomActions getMessageText).
      const pinnedOppId = resolveLeadOpportunityId(messageOpportunity);
      const res = await createOttoVinLeadLinks({
        variables: {
          leadId: +selectedLead.id,
          vehicleIds: vehicleIds.map(id => +id),
          leadOpportunityId: pinnedOppId || null,
          selectedOppTypeId: pinnedOppId ? null : selectedOppTypeId || null,
        },
      });
      const payload = res?.data?.createOttoVinLeadLinks;
      if (payload?.statusCode !== 200) {
        message.error(payload?.message || "Could not generate OttoVin links");
        return null;
      }
      const links = payload.links || [];
      const succeeded = links.filter(link => link.shortUrl && !link.error);
      const failed = links.length - succeeded.length;
      if (failed > 0) {
        message.warning(`${failed} OttoVin link(s) could not be generated`);
      }
      if (succeeded.length === 0) {
        message.error("No OttoVin links were generated");
        return null;
      }
      message.success("OttoVin links added");
      return Object.fromEntries(
        succeeded.map(link => [String(link.vin || "").toUpperCase(), link.shortUrl])
      );
    },
    [createOttoVinLeadLinks, selectedLead]
  );

  const handleAddLinks = async () => {
    if (selectorRef.current) {
      setIsLoading(true);
      try {
        await selectorRef.current.addVehicles();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const footer = (
    <div className="flex justify-start gap-3">
      <Button
        type="primary"
        loading={isLoading}
        disabled={selectedVehicles?.length === 0}
        onClick={handleAddLinks}
      >
        Add Links
      </Button>
      <Button onClick={onClose}>Cancel</Button>
    </div>
  );

  return (
    <Drawer
      title="Select Vehicle From Inventory — OttoVin"
      placement="right"
      size="large"
      onClose={onClose}
      open={open}
      width={600}
      footer={footer}
      styles={{ body: { padding: "1rem" } }}
    >
      <VDPSelector
        ref={selectorRef}
        text={text}
        setmessageText={setmessageText}
        handleInsertLink={handleInsertLink}
        onClose={onClose}
        onSelectedVehiclesChange={onSelectedVehiclesChange}
        resolveLinks={resolveLinks}
        ottoVinInsert
        vehicleOfInterest={vehicleOfInterest}
      />
    </Drawer>
  );
};

export default OttoVinPanel;
